import React, { useState, useEffect } from "react";
import {
  Grid,
  useTheme,
  Typography,
  Box,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  AlertTitle,
  IconButton,
  CircularProgress,
  Fade,
  Button,
  useMediaQuery,
} from "@mui/material";
import { Refresh, ErrorOutline, Tune } from "@mui/icons-material";
import SearchBar from "../components/SearchBar";
import FilterModal from "../components/FilterModal";
import SortButton from "../components/SortButton";
import BackWithBreadcrumbAppbar from "../components/BackWithBreadcrumbAppbar";
import { useQuery, gql } from "@apollo/client";
import LoadingPage from "./LoadingPage";
import { useTranslation } from "react-i18next";
import FilteredAttendees from "../components/FilteredAttendees";
import FilterButton from "../components/FilterButton";
import useUserData from "../hooks/useUserData";

const GET_ALL_ATTENDEES = gql`
  query GetAllAttendees($roleType: String) {
    getAllAttendees(roleType: $roleType) {
      id
      name
      designation
      company
      country
      role
      email
      isConnected
      status
      created
      bio
      stallNo
      profile_pic
    }
  }
`;

const AttendeeList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [attendees, setAttendees] = useState([]);
  const [filteredAttendees, setFilteredAttendees] = useState([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    role: [],
    company: [],
    designation: [],
    country: [],
  });
  const { data: user_data, refetch: userRefetch } = useUserData();
  let userdata = user_data?.me;
  if (typeof user_data?.me === "string") {
    userdata = JSON.parse(user_data?.me);
  }
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const user = userdata;

  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const roleType = "All";

  const { loading, error, data, refetch } = useQuery(GET_ALL_ATTENDEES, {
    variables: { roleType },
    notifyOnNetworkStatusChange: true,
    skip: !roleType,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (data?.getAllAttendees) {
      const filteredData = data.getAllAttendees
        .filter(
          (attendee) =>
            !attendee.isConnected &&
            attendee.email !== user.email &&
            attendee.role !== "speaker"
        )
        // Add sorting by name
        .sort((a, b) => a?.name?.localeCompare(b?.name));

      setAttendees(filteredData);
      setFilteredAttendees(filteredData);
    }
  }, [data]);

  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortOption, setSortOption] = useState("");

  useEffect(() => {
    let filtered = [...attendees];

    // Apply search first
    if (searchQuery) {
      const searchLower = searchQuery?.toLowerCase();
      filtered = filtered?.filter((attendee) =>
        attendee.name?.toLowerCase().includes(searchLower)
      );
    }

    // Apply filters
    if (filters.role.length > 0) {
      filtered = filtered.filter((a) => filters.role.includes(a.role));
    }
    if (filters.company.length > 0) {
      filtered = filtered.filter((a) => filters.company.includes(a.company));
    }
    if (filters.designation.length > 0) {
      filtered = filtered.filter((a) =>
        filters.designation.includes(a.designation)
      );
    }
    if (filters.country.length > 0) {
      filtered = filtered.filter((a) => filters.country.includes(a.country));
    }

    // Apply sorting
    if (sortOption) {
      filtered.sort((a, b) => {
        let field;
        if (sortOption === "name") {
          field = "name";
        } else if (sortOption === "company") {
          field = "company";
        } else if (sortOption === "designation") {
          field = "designation";
        } else {
          field = "country";
        }
        return a[field].localeCompare(b[field]);
      });
    }

    setFilteredAttendees(filtered);
    setCurrentPage(1);
  }, [attendees, searchQuery, filters, sortOption]);

  const handleFilterApply = (newFilters) => {
    setFilters(newFilters);
    setFilterModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchQuery(event);
  };

  const handleFilter = () => {
    setFilterModalOpen(true);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (option) => {
    setSortOption(option);
    setSortAnchorEl(null);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAttendees = filteredAttendees.slice(startIndex, endIndex);
  const handleRetry = () => {
    refetch();
  };

  if (error) {
    return (
      <>
        <BackWithBreadcrumbAppbar
          links={[{ label: t("dashboard"), href: "/" }]}
          currentPage={t("attendees")}
        />
        <Box sx={{ p: 3, height: "100vh" }}>
          <Alert
            severity="error"
            sx={{
              mt: 4,
              borderRadius: 2,
              boxShadow: theme.shadows[2],
            }}
            action={
              <IconButton color="inherit" onClick={handleRetry}>
                <Refresh />
              </IconButton>
            }
          >
            <AlertTitle>{t("Error")}</AlertTitle>
            {t("404")} ({error?.message || "Something went wrong"})
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <BackWithBreadcrumbAppbar
        links={[{ label: t("dashboard"), href: "/" }]}
        currentPage={t("attendees")}
      />
      <Box
        sx={{
          padding: theme.spacing(1),
          position: "relative",
          maxWidth: 1600,
          margin: "0 auto",
        }}
      >
        <Fade in={!loading} timeout={300}>
          <Box>
            <Typography
              variant="h3"
              align="center"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 700,
                mb: 3,
                textShadow: `0 2px 4px ${theme.palette.action.hover}`,
              }}
            >
              {t("attendeesList")}
            </Typography>

            <Grid
              container
              spacing={1.5}
              alignItems="center"
              sx={{
                position: "sticky",
                top: isMobile ? 100 : 130,
                zIndex: 10,
                backgroundColor: theme.palette.background.paper,
                py: 2,
                px: 1,
                boxShadow: `0 2px 8px ${
                  theme.palette.mode === "dark"
                    ? "rgba(0,0,0,0.3)"
                    : "rgba(0,0,0,0.1)"
                }`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                marginBottom: 3,
                transition: "all 0.2s ease",
              }}
            >
              {/* Search Bar */}
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <SearchBar
                  role="attendees"
                  onSearch={handleSearch}
                  attendees={attendees}
                  placeholder={t("searchAttendees")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: theme.palette.background.default,
                      border: `1px solid ${theme.palette.divider}`,
                      transition: theme.transitions.create([
                        "border-color",
                        "box-shadow",
                      ]),
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                      "&.Mui-focused": {
                        boxShadow: `${theme.shadows[2]}`,
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>

              {/* Filter & Sort Controls */}
              <Grid
                item
                xs={8}
                md={4}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  justifyContent: "center",
                }}
              >
                {/* Filter Button */}
                <Box sx={{ flex: 1 }}>
                  <FilterButton
                    onFilter={handleFilter}
                    badgeContent={Object.values(filters).flat().length}
                    sx={{
                      width: "100%",
                      borderRadius: 3,
                      py: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  />
                </Box>

                {/* Sort Button */}
                <Box sx={{ flex: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleSortClick}
                    startIcon={<Tune fontSize="small" />}
                    sx={{
                      width: "100%",
                      borderRadius: 3,
                      py: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {t("sort")}
                  </Button>
                  <Menu
                    anchorEl={sortAnchorEl}
                    open={Boolean(sortAnchorEl)}
                    onClose={handleSortClose}
                    PaperProps={{
                      sx: {
                        borderRadius: 2,
                        boxShadow: theme.shadows[3],
                        minWidth: 150,
                        mt: 1,
                        border: `1px solid ${theme.palette.divider}`,
                      },
                    }}
                  >
                    {["name", "company", "designation", "country"].map(
                      (option) => (
                        <MenuItem
                          key={option}
                          onClick={() => handleSortSelect(option)}
                          selected={sortOption === option}
                          sx={{
                            "&.Mui-selected": {
                              backgroundColor: theme.palette.action.selected,
                              fontWeight: 500,
                            },
                            py: 1,
                            fontSize: "0.9rem",
                          }}
                        >
                          {t(option)}
                        </MenuItem>
                      )
                    )}
                  </Menu>
                </Box>
              </Grid>

              {/* Items Per Page Selector */}
              <Grid
                item
                xs={4}
                md={4}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                }}
              >
                <FormControl
                  variant="outlined"
                  sx={{
                    minWidth: { xs: 80, sm: 100 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: theme.palette.background.default,
                      border: `1px solid ${theme.palette.divider}`,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <InputLabel shrink>{t("itemsPerPage")}</InputLabel>
                  <Select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    label={t("itemsPerPage")}
                    sx={{
                      "& .MuiSelect-select": {
                        py: 1.1,
                        pl: 2,
                      },
                    }}
                  >
                    {[10, 25, 50, 100].map((value) => (
                      <MenuItem
                        key={value}
                        value={value}
                        sx={{
                          borderRadius: 1,
                          m: 0.5,
                          "&.Mui-selected": {
                            backgroundColor: `${theme.palette.action.selected} !important`,
                          },
                        }}
                      >
                        {value}
                      </MenuItem>
                    ))}
                    <MenuItem
                      value={attendees.length}
                      sx={{
                        borderRadius: 1,
                        m: 0.5,
                        borderTop: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {t("all")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <FilteredAttendees
              role="attendees"
              refresh={refetch}
              filteredAttendees={filteredAttendees}
              paginatedAttendees={paginatedAttendees}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              handlePageChange={handlePageChange}
              setFilteredAttendees={setFilteredAttendees}
              user={user}
            />

            <FilterModal
              open={filterModalOpen}
              onClose={() => setFilterModalOpen(false)}
              onApply={handleFilterApply}
              filters={filters}
              attendees={attendees}
              roleName="attendees"
            />
          </Box>
        </Fade>

        {loading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.8)",
              zIndex: theme.zIndex.modal,
            }}
          >
            <CircularProgress
              size={80}
              thickness={2}
              sx={{
                color: theme.palette.primary.main,
                animationDuration: "800ms",
              }}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default AttendeeList;
